import { useLayoutEffect, useEffect } from "react";

/**
 * Injects a per-route canonical + hreflang set into the document <head>.
 *
 * Uses `useLayoutEffect` on the client so the tags are in the DOM before
 * paint — which is critical for `react-snap`, whose puppeteer snapshot
 * heuristic can serialize the DOM right after mount. If we used a plain
 * `useEffect`, some routes ended up frozen with the index.html default
 * canonical (`https://npdlbr.com.br/`), telling Google every page was a
 * duplicate of the homepage.
 *
 * SSR-safe fallback: uses a no-op `useEffect` during server render.
 *
 * We do NOT restore the previous href on unmount: react-snap treats each
 * route as a separate page load, and in the SPA the next route's hook will
 * overwrite the tags anyway.
 *
 * Usage: `useCanonical("/blog");`
 */
const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

const HREFLANGS = ["pt-BR", "en", "fr", "x-default"];

export function useCanonical(pathname) {
  useIsoLayoutEffect(() => {
    const url = `https://npdlbr.com.br${pathname || ""}`;

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);
    canonical.removeAttribute("data-default-canonical");

    // Hreflang set (self-referential — SPA with client-side i18n)
    HREFLANGS.forEach((lg) => {
      let tag = document.head.querySelector(
        `link[rel="alternate"][hreflang="${lg}"]`
      );
      if (!tag) {
        tag = document.createElement("link");
        tag.setAttribute("rel", "alternate");
        tag.setAttribute("hreflang", lg);
        document.head.appendChild(tag);
      }
      tag.setAttribute("href", url);
      tag.removeAttribute("data-default-hreflang");
    });
  }, [pathname]);
}
